import React, { useEffect, useState, useCallback, useMemo } from 'react'
import {
  getQueueData,
  getServerStatus,
  getUserTasksData,
} from './HomepageServices'
import {
  Box,
  CardActionArea,
  CardContent,
  Dialog,
  Stack,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Skeleton,
  Paper,
  Chip,
  Fade,
  Zoom,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Card,
  Avatar,
  Divider,
} from '@mui/material'
import { useSelector } from 'react-redux'
import TaskResultDetails from './TaskResultDetails'
import BackupIcon from '@mui/icons-material/Backup'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import ScienceIcon from '@mui/icons-material/Science'
import BiotechIcon from '@mui/icons-material/Biotech'
import PsychologyIcon from '@mui/icons-material/Psychology'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import RefreshIcon from '@mui/icons-material/Refresh'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SpeedIcon from '@mui/icons-material/Speed'
import AgGridWrapper from '../../components/common/AgGridWrapper'

// Constants
const SERVER_TYPES = {
  DOCK: 'dock',
  OMEGA: 'omega', 
  LIGTMAP: 'ligtmap'
}

const SERVICE_CARDS_CONFIG = [
  {
    id: 'dock',
    title: 'Molecular Docking',
    subtitle: 'Protein-ligand interactions',
    description: 'High-precision binding affinity prediction',
    statusKey: 'dockingStatus',
    queueKey: 'dock',
    icon: ScienceIcon,
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#667eea',
    shadowColor: 'rgba(102, 126, 234, 0.3)'
  },
  {
    id: 'dtp',
    title: 'Drug Target Prediction', 
    subtitle: 'AI-powered target identification',
    description: 'Machine learning-driven target discovery',
    statusKey: 'dtpStatus',
    queueKey: 'dtp',
    icon: BiotechIcon,
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    color: '#f093fb',
    shadowColor: 'rgba(240, 147, 251, 0.3)'
  },
  {
    id: 'psp',
    title: 'Protein Structure Prediction',
    subtitle: 'Advanced folding algorithms',
    description: 'AlphaFold-inspired structure prediction',
    statusKey: 'pspStatus', 
    queueKey: 'psp',
    icon: PsychologyIcon,
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    color: '#4facfe',
    shadowColor: 'rgba(79, 172, 254, 0.3)'
  },
  {
    id: 'admet',
    title: 'ADMET AI',
    subtitle: 'Pharmacokinetic properties',
    description: 'Real-time drug property analysis',
    statusKey: 'admetStatus',
    queueKey: 'admet',
    alwaysOnline: true,
    icon: AutoAwesomeIcon,
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    color: '#43e97b',
    shadowColor: 'rgba(67, 233, 123, 0.3)'
  }
]

// Utility functions
const parseJsonSafely = (data) => {
  if (!data) return null
  return typeof data === 'string' ? JSON.parse(data) : data
}

// AG Grid Cell Renderers
const DetailsButtonRenderer = ({ data, handleDialogOpen }) => {
  const handleClick = useCallback(() => {
    if (data?.smiles) {
      handleDialogOpen(data.smiles)
    }
  }, [data?.smiles, handleDialogOpen])

  return (
    <Button 
      variant="contained" 
      onClick={handleClick}
      size="small"
      disabled={!data?.smiles}
      sx={{
        background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
        '&:hover': {
          background: 'linear-gradient(45deg, #1565c0 0%, #1976d2 100%)',
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
        },
        transition: 'all 0.2s ease-in-out'
      }}
    >
      View Details
    </Button>
  )
}

const ProgressRenderer = ({ value }) => {
  const progressValue = Math.max(0, Math.min(100, value || 0))
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', mr: 2 }}>
        <LinearProgress 
          variant="determinate" 
          value={progressValue}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: 'rgba(0,0,0,0.08)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              background: progressValue === 100 
                ? 'linear-gradient(90deg, #43a047, #66bb6a)'
                : progressValue > 50
                ? 'linear-gradient(90deg, #1976d2, #42a5f5)'
                : 'linear-gradient(90deg, #fb8c00, #ffb74d)'
            }
          }}
        />
      </Box>
      <Box sx={{ minWidth: 50 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 700,
            fontSize: '0.8rem',
            color: progressValue === 100 ? '#43a047' : progressValue > 50 ? '#1976d2' : '#fb8c00'
          }}
        >
          {`${Math.round(progressValue)}%`}
        </Typography>
      </Box>
    </Box>
  )
}

// Enhanced Service Status Card Component
const ServiceStatusCard = ({ 
  title, 
  subtitle,
  description,
  isOnline, 
  pendingItems, 
  alwaysOnline = false,
  icon: IconComponent,
  gradient,
  color,
  shadowColor
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const isServiceOnline = isOnline || alwaysOnline

  return (
    <Card
      elevation={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        height: '200px',
        borderRadius: '20px',
        background: 'white',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered 
          ? `0 12px 24px ${shadowColor}` 
          : '0 4px 12px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.04)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradient,
        }
      }}
    >
      <CardContent sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        p: 3,
        '&:last-child': { pb: 3 }
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Avatar
              sx={{
                background: gradient,
                width: 40,
                height: 40,
                mr: 2,
                boxShadow: `0 4px 8px ${shadowColor}`
              }}
            >
              <IconComponent sx={{ color: 'white', fontSize: '1.2rem' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#1a1a1a', 
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  mb: 0.5
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666', 
                  fontSize: '0.8rem',
                  fontWeight: 500
                }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>
          
          <Chip
            icon={isServiceOnline ? <BackupIcon sx={{ fontSize: '0.8rem' }} /> : <CloudOffIcon sx={{ fontSize: '0.8rem' }} />}
            label={isServiceOnline ? 'Online' : 'Offline'}
            size="small"
            variant="outlined"
            sx={{
              borderColor: isServiceOnline ? '#43a047' : '#f44336',
              color: isServiceOnline ? '#43a047' : '#f44336',
              backgroundColor: isServiceOnline ? 'rgba(67, 160, 71, 0.08)' : 'rgba(244, 67, 54, 0.08)',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '24px',
              '& .MuiChip-icon': {
                color: isServiceOnline ? '#43a047' : '#f44336'
              }
            }}
          />
        </Box>

        {/* Description */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#888',
            fontSize: '0.75rem',
            lineHeight: 1.4,
            mb: 2,
            flex: 1
          }}
        >
          {description}
        </Typography>

        {/* Stats Section */}
        <Box sx={{ 
          backgroundColor: '#f8f9fa', 
          borderRadius: '12px', 
          p: 2,
          border: '1px solid rgba(0,0,0,0.04)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  fontWeight: 600,
                  display: 'block',
                  mb: 0.5
                }}
              >
                Queue Status
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#1a1a1a', 
                  fontWeight: 800,
                  lineHeight: 1,
                  fontSize: '1.5rem'
                }}
              >
                {pendingItems || 0}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#888',
                  fontSize: '0.65rem'
                }}
              >
                pending tasks
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: isServiceOnline ? '#43a047' : '#bbb'
            }}>
              <SpeedIcon sx={{ fontSize: '1.5rem', opacity: 0.7 }} />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

// Enhanced Loading skeleton
const ServiceCardsSkeleton = () => (
  <Grid container spacing={3}>
    {[...Array(4)].map((_, index) => (
      <Grid item xs={12} sm={6} lg={3} key={index}>
        <Card
          sx={{
            height: 200,
            borderRadius: '20px',
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={14} />
            </Box>
          </Box>
          <Skeleton variant="text" width="90%" height={12} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" width="100%" height={50} />
        </Card>
      </Grid>
    ))}
  </Grid>
)

// Enhanced Stats Cards
const StatsCard = ({ title, value, icon: IconComponent, trend, color }) => (
  <Card
    elevation={0}
    sx={{
      p: 3,
      borderRadius: '16px',
      background: 'white',
      border: '1px solid rgba(0,0,0,0.06)',
      height: '120px',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${color}, ${color}80)`
      }
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#666',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: 600,
            mb: 1,
            display: 'block'
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1a1a1a',
            fontWeight: 800,
            lineHeight: 1,
            mb: 1
          }}
        >
          {value}
        </Typography>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUpIcon sx={{ fontSize: '1rem', color: '#43a047', mr: 0.5 }} />
            <Typography variant="caption" sx={{ color: '#43a047', fontWeight: 600 }}>
              {trend}
            </Typography>
          </Box>
        )}
      </Box>
      <Avatar
        sx={{
          background: `${color}15`,
          color: color,
          width: 56,
          height: 56
        }}
      >
        <IconComponent sx={{ fontSize: '1.5rem' }} />
      </Avatar>
    </Box>
  </Card>
)

// Main HomePage Component
const HomePage = () => {
  // State management
  const [serviceStatuses, setServiceStatuses] = useState({
    dtpStatus: false,
    dockingStatus: false, 
    pspStatus: false,
    admetStatus: true
  })
  // Define state variables
  const [currentStatus, setCurrentStatus] = useState(null);
  const [dockingStatus, setDockingStatus] = useState(false);
  const [pspStatus, setPspStatus] = useState(false);
  const [dtpStatus, setDtpStatus] = useState(false);

  const [queueData, setQueueData] = useState({})
  const [userTaskData, setUserTaskData] = useState([])
  const [selectedTask, setSelectedTask] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const { userId } = useSelector((state) => state.auth)

  // Memoized event handlers
  const handleDialogOpen = useCallback((smiles) => {
    setSelectedTask(smiles)
    setIsDialogOpen(true)
  }, [])

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false)
    setSelectedTask('')
  }, [])


  useEffect(() => {
    // Improved error handling for data fetching
    const fetchData = async () => {
      try {
        // Fetch queue data
        const queueData = await getQueueData()
        if (queueData) {
          const parsedQueueData = typeof queueData === 'string' ? JSON.parse(queueData) : queueData
          setCurrentStatus(parsedQueueData)
        }

        // Fetch server statuses
        const [dockStatus, omegaStatus, ligtmapStatus] = await Promise.all([
          getServerStatus('dock').catch(e => { console.error('Dock status error:', e); return false; }),
          getServerStatus('omega').catch(e => { console.error('Omega status error:', e); return false; }),
          getServerStatus('ligtmap').catch(e => { console.error('Ligtmap status error:', e); return false; })
        ])

        setDockingStatus(Boolean(dockStatus))
        setPspStatus(Boolean(omegaStatus))
        setDtpStatus(Boolean(ligtmapStatus))

        // Fetch user tasks
        if (userId) {
          const taskData = await getUserTasksData(userId)
          if (taskData) {
            const parsedTaskData = typeof taskData === 'string' ? JSON.parse(taskData) : taskData;
            setUserTaskData(Array.isArray(parsedTaskData)? parsedTaskData : []);
          }
        }
      } catch (error) {
        console.error('Error fetching homepage data:', error)
      }
    }

    fetchData()
  }, [userId])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    window.location.reload()
  }, [])


  // Enhanced column definitions with safe center alignment
  const columnDefs = useMemo(() => [
    {
      headerName: 'Compound Name',
      minWidth: 30 ,
      sortable: true,
      field: 'compound_name',
    },
    {
      headerName: 'SMILES Structure',
      field: 'smiles',
      minWidth: 280,
      sortable: true,
      filter: true,
      cellStyle: { 
        fontFamily: '"JetBrains Mono", Monaco, monospace', 
        fontSize: '0.85rem',
        color: '#1976d2',
        fontWeight: 500,
        padding: '12px 16px',
        textAlign: 'center'
      }
    },
    {
      headerName: 'Library',
      field: 'library',
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          variant="outlined"
          sx={{ 
            borderColor: '#2196f3',
            color: '#1976d2',
            fontWeight: 600,
            fontSize: '0.75rem',
            backgroundColor: 'rgba(33, 150, 243, 0.05)'
          }}
        />
      ),
      cellStyle: { 
        padding: '12px 16px',
        textAlign: 'center'
      }
    },
    {
      headerName: 'Method',
      field: 'method',
      minWidth: 150,
      sortable: true,
      filter: true,
      cellStyle: { 
        fontWeight: 600,
        color: '#333',
        padding: '12px 16px',
        textAlign: 'center'
      }
    },
    {
      headerName: 'Created',
      field: 'created_date',
      minWidth: 140,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        }
        return ''
      },
      cellStyle: { 
        color: '#666',
        fontSize: '0.85rem',
        padding: '12px 16px',
        textAlign: 'center'
      }
    },
    {
      headerName: 'Progress',
      field: 'progress',
      minWidth: 180,
      sortable: true,
      cellRenderer: ProgressRenderer,
      cellStyle: { 
        padding: '12px 16px',
        textAlign: 'center'
      }
    },
    {
      headerName: 'Actions',
      minWidth: 140,
      sortable: false,
      cellRenderer: DetailsButtonRenderer,
      cellRendererParams: {
        handleDialogOpen,
      },
      cellStyle: { 
        padding: '12px 16px',
        textAlign: 'center'
      }
    },
  ], [handleDialogOpen])

  // Calculate stats
  const totalTasks = userTaskData.length
  const completedTasks = userTaskData.filter(task => task.progress === 100).length
  const activeTasks = userTaskData.filter(task => task.progress > 0 && task.progress < 100).length

  // Data fetching effect
  useEffect(() => {
    let isMounted = true

    const fetchAllData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch queue data
        const queueDataResponse = await getQueueData()
        if (isMounted && queueDataResponse) {
          const parsedQueueData = parseJsonSafely(queueDataResponse)
          setQueueData(parsedQueueData || {})
        }

        // Fetch server statuses in parallel
        const statusPromises = [
          getServerStatus(SERVER_TYPES.DOCK),
          getServerStatus(SERVER_TYPES.OMEGA),
          getServerStatus(SERVER_TYPES.LIGTMAP)
        ].map(promise => 
          promise.catch(error => {
            console.warn('Server status check failed:', error)
            return false
          })
        )

        const [dockStatus, omegaStatus, ligtmapStatus] = await Promise.all(statusPromises)

        if (isMounted) {
          setServiceStatuses(prev => ({
            ...prev,
            dockingStatus: Boolean(dockStatus),
            pspStatus: Boolean(omegaStatus),
            dtpStatus: Boolean(ligtmapStatus),
          }))
        }

        // Fetch user tasks if userId is available
        if (userId && isMounted) {
          try {
            const taskDataResponse = await getUserTasksData(userId)
            if (taskDataResponse) {
              const parsedTaskData = parseJsonSafely(taskDataResponse)
              setUserTaskData(Array.isArray(parsedTaskData) ? parsedTaskData : [])
            }
          } catch (taskError) {
            console.error('Error fetching user tasks:', taskError)
          }
        }

      } catch (error) {
        console.error('Error fetching homepage data:', error)
        if (isMounted) {
          setError('Failed to load dashboard data. Please try refreshing.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
          setRefreshing(false)
        }
      }
    }

    fetchAllData()

    return () => {
      isMounted = false
    }
  }, [userId])

  // Render error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            border: '1px solid rgba(244, 67, 54, 0.2)',
            backgroundColor: 'rgba(244, 67, 54, 0.05)'
          }}
        >
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
          sx={{
            background: 'linear-gradient(45deg, #f44336 30%, #ff9800 90%)',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.5
          }}
        >
          Refresh Dashboard
        </Button>
      </Container>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Enhanced Header */}
        <Fade in={true} timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #26c6da 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 1,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                letterSpacing: '-0.02em'
              }}
            >
              DiscoveraAI
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#64748b',
                fontWeight: 400,
                mb: 3,
                fontSize: { xs: '1.2rem', md: '1.5rem' }
              }}
            >
              DeepTech Drug Discovery Platform
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
              <Tooltip title="Refresh Dashboard">
                <IconButton 
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{ 
                    background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                    color: 'white',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0, #1976d2)',
                      transform: 'scale(1.05)'
                    },
                    '&:disabled': {
                      background: '#ccc'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <RefreshIcon className={refreshing ? 'spin' : ''} />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Divider sx={{ width: '60px', mx: 'auto', height: '3px', background: 'linear-gradient(90deg, #1976d2, #42a5f5)' }} />
          </Box>
        </Fade>

        {/* Stats Overview */}
        {userId && (
          <Fade in={true} timeout={1000}>
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <StatsCard
                    title="Total Tasks"
                    value={totalTasks}
                    icon={ScienceIcon}
                    color="#1976d2"
                    trend="+12% this week"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatsCard
                    title="Completed"
                    value={completedTasks}
                    icon={BackupIcon}
                    color="#43a047"
                    trend="+8% this week"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatsCard
                    title="Active"
                    value={activeTasks}
                    icon={TrendingUpIcon}
                    color="#fb8c00"
                  />
                </Grid>
              </Grid>
            </Box>
          </Fade>
        )}

        {/* Service Status Cards Section */}
        <Fade in={true} timeout={1200}>
          <Paper 
            elevation={0}
            sx={{ 
              borderRadius: '24px', 
              p: 4, 
              mb: 4,
              background: 'white',
              border: '1px solid rgba(0,0,0,0.06)'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  color: '#1a1a1a',
                  mb: 1
                }}
              >
                AI Services
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '1.1rem'
                }}
              >
                Real-time monitoring of computational services
              </Typography>
            </Box>
            
            {loading ? (
              <ServiceCardsSkeleton />
            ) : (
              <Grid container spacing={3}>
                {SERVICE_CARDS_CONFIG.map((config, index) => (
                  <Grid item xs={12} sm={6} lg={3} key={config.id}>
                    <ServiceStatusCard
                      title={config.title}
                      subtitle={config.subtitle}
                      description={config.description}
                      isOnline={serviceStatuses[config.statusKey]}
                      pendingItems={queueData[config.queueKey]}
                      alwaysOnline={config.alwaysOnline}
                      icon={config.icon}
                      gradient={config.gradient}
                      color={config.color}
                      shadowColor={config.shadowColor}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Fade>

        {/* User Tasks Table Section */}
        <Fade in={true} timeout={1400}>
          <Paper 
            elevation={0}
            sx={{ 
              borderRadius: '24px', 
              p: 4,
              background: 'white',
              border: '1px solid rgba(0,0,0,0.06)',
              minHeight: '600px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#1a1a1a',
                    mb: 1
                  }}
                >
                  Task Management
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#64748b',
                    fontSize: '1.1rem'
                  }}
                >
                  Monitor and analyze your computational workflows
                </Typography>
              </Box>
              
              {userTaskData.length > 0 && (
                <Chip 
                  label={`${userTaskData.length} total tasks`}
                  sx={{ 
                    background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    px: 2,
                    py: 1,
                    height: 'auto'
                  }}
                />
              )}
            </Box>
            
            {!userId ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                flex: 1,
                py: 6
              }}>
                <Avatar sx={{ 
                  width: 80, 
                  height: 80, 
                  mb: 3,
                  background: 'linear-gradient(135deg, #64b5f6, #42a5f5)',
                  fontSize: '2rem'
                }}>
                  🔐
                </Avatar>
                <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 600, mb: 2 }}>
                  Authentication Required
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', maxWidth: 400 }}>
                  Please log in to access your computational tasks, view results, and manage your drug discovery workflows.
                </Typography>
              </Box>
            ) : userTaskData.length === 0 && !loading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                flex: 1,
                py: 6
              }}>
                <Avatar sx={{ 
                  width: 80, 
                  height: 80, 
                  mb: 3,
                  background: 'linear-gradient(135deg, #81c784, #66bb6a)',
                  fontSize: '2rem'
                }}>
                  🧬
                </Avatar>
                <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 600, mb: 2 }}>
                  Ready for Discovery
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', maxWidth: 400 }}>
                  Your task queue is empty. Start your next drug discovery project using our AI-powered computational services.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, height: '100%' }}>
                <AgGridWrapper
                  data={userTaskData}
                  headers={columnDefs}
                  loading={loading}
                  onRowClick={handleDialogOpen}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: true,
                    cellStyle: { 
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                    }
                  }}
                  pagination={true}
                  paginationPageSize={15}
                  rowHeight={64}
                  headerHeight={56}
                  gridOptions={{
                    rowSelection: 'single',
                    animateRows: true,
                    enableCellTextSelection: true,
                    suppressRowClickSelection: false,
                    rowStyle: {
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    },
                    getRowStyle: (params) => {
                      return {
                        background: params.node.rowIndex % 2 === 0 ? '#fafbfc' : 'white',
                        '&:hover': {
                          backgroundColor: '#f0f9ff !important',
                          transform: 'scale(1.01)'
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}
          </Paper>
        </Fade>

        {/* Task Details Dialog */}
        <Dialog 
          fullScreen 
          open={isDialogOpen} 
          onClose={handleDialogClose}
          aria-labelledby="task-details-dialog"
          TransitionComponent={Fade}
          sx={{
            '& .MuiDialog-paper': {
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            }
          }}
        >
          <TaskResultDetails
            smiles={selectedTask}
            handleOnClose={handleDialogClose}
          />
        </Dialog>
      </Container>

      {/* Enhanced CSS animations */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }

        /* AG Grid center alignment styles */
        .ag-theme-alpine .ag-header-cell-label {
          justify-content: center;
        }
        
        .ag-theme-alpine .ag-cell {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        /* Custom scrollbar for grid */
        .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar,
        .ag-theme-alpine .ag-body-vertical-scroll::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar-track,
        .ag-theme-alpine .ag-body-vertical-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar-thumb,
        .ag-theme-alpine .ag-body-vertical-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #1976d2, #42a5f5);
          border-radius: 4px;
        }
        
        .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar-thumb:hover,
        .ag-theme-alpine .ag-body-vertical-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #1565c0, #1976d2);
        }
      `}</style>
    </Box>
  )
}

export default HomePage
